import { AppConfigurationService, AppSettingService } from '@shopiroller/core';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  UpdateUserProfileModel,
  UserProfileService,
  loadUserProfileInformation,
  selectUserProfileInformation
} from '@shopiroller/user-profile';
import {
  checkUserMembershipStatusInAppSumo,
  selectUserAppSumoMembershipStatus
} from '@shopiroller/appsumo';
import { AuthService } from './modules/auth';
import { AutoUnsubscribe } from '@shopiroller/decorators';
import { LOCAL_STORAGE_KEYS, featuresList } from '@shopiroller/data';
import { LayoutService } from './shared/@metronic/core';
import { SendyService, ISendySubscribe } from '@shopiroller/sendy';
import { SplashScreenService } from './shared/@metronic/partials/layout/splash-screen/splash-screen.service';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService, EN_LOCALE, TR_LOCALE } from '@shopiroller/i18n';
import { UserService } from './modules/auth/data-access';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { of } from 'rxjs/internal/observable/of';

@AutoUnsubscribe()
@Component({
  selector: 'body[root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  private _subs = new SubSink();

  constructor(
    private layout: LayoutService,
    private translationService: TranslationService,
    private splashScreenService: SplashScreenService,
    private router: Router,
    private appSettingService: AppSettingService,
    private userProfileService: UserProfileService,
    private translateService: TranslateService,
    private authService: AuthService,
    private sendyService: SendyService,
    private appConfigurationService: AppConfigurationService,
    private userService: UserService,
    private readonly _store: Store
  ) {
    this.appSettingService.initializeSettings();
    this.translationService.loadTranslations(EN_LOCALE, TR_LOCALE);
    this.setDefaultConfigOfApp();
  }

  ngOnInit(): void {
    let eventsDispatchedAfterRefresh = false;
    this._subs.sink = this.router.events.subscribe((event) => {
      const routerSubscription = this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (event.id === 1 && event.url === event.urlAfterRedirects) {
            // After refresh event will be executed
            if (
              !eventsDispatchedAfterRefresh &&
              this.authService.authenticated
            ) {
              eventsDispatchedAfterRefresh = true;
              this._dispatchCommandEvents();
              this.subscribeSendy();
            }
          }
          // hide splash screen
          this.splashScreenService.hide();

          // scroll to top on every route change
          window.scrollTo(0, 0);

          // to display back the body content
          setTimeout(() => {
            document.body.classList.add('page-loaded');
            this.layout.resetAsideMenuDisplayMode();
          }, 500);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _dispatchCommandEvents(): void {
    this._store.dispatch(loadUserProfileInformation());
    this._store.dispatch(checkUserMembershipStatusInAppSumo());
  }

  private setDefaultConfigOfApp(): void {
    const defaultLanguage = this._getDefaultLanguage();
    if (defaultLanguage.baseBrowserLanguage && this.authService.authenticated) {
      this._subs.sink = this._store
        .select(selectUserProfileInformation)
        .pipe(
          concatMap((userInfo) => {
            if (!userInfo) return of(null);
            const updatedUser: UpdateUserProfileModel = {
              language: defaultLanguage.language,
              fullname: userInfo.fullName
            };
            return this.userProfileService.updateUserProfile(updatedUser);
          })
        )
        .subscribe((result) => {
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.USER_LANGUAGE,
            defaultLanguage.language
          );
        });
    }
    this.translateService.setDefaultLang(defaultLanguage.language);
  }

  private _getDefaultLanguage(): {
    language: string;
    baseBrowserLanguage: boolean;
  } {
    const userLanguage = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_LANGUAGE);
    if (userLanguage != null) {
      return {
        language: userLanguage,
        baseBrowserLanguage: false
      };
    } else {
      const browserLanguage =
        navigator.language == undefined
          ? 'tr'
          : navigator.language.toLowerCase().substring(0, 2);
      let browserLanguages =
        navigator.languages === undefined ? ['tr'] : navigator.languages;
      browserLanguages = browserLanguages.map((language) =>
        language.toLowerCase().substring(0, 2)
      );
      let defaultLanguage = browserLanguages.some((x) => x == 'tr')
        ? 'tr'
        : 'en';
      if (defaultLanguage != 'tr' && browserLanguage == 'tr') {
        defaultLanguage = 'tr';
      }
      return {
        language: defaultLanguage,
        baseBrowserLanguage: true
      };
    }
  }

  private subscribeSendy(): void {
    const sendyFeatureIsEnabled = this.appConfigurationService.isFeatureEnabled(
      featuresList.sendy
    );
    const currentUser = this.userService.user;
    if (sendyFeatureIsEnabled && currentUser) {
      this._subs.sink = this._store
        .select(selectUserAppSumoMembershipStatus)
        .subscribe((membershipStatus) => {
          if (membershipStatus != null) {
            const language = this._getDefaultLanguage().language;
            const payload: ISendySubscribe = {
              appSumo: membershipStatus + '',
              email: currentUser.email,
              name: currentUser.fullName,
              language: language
            };
            this.sendyService.subscribe(payload).subscribe();
          }
        });
    }
  }
}
